import { toast } from "sonner";
import type { HoldActionResult, BulkHoldResult } from "@workspace/api-client-react";

type HoldAction = "hold" | "unhold";

// On-demand "Refresh holds" re-check. The verify-holds endpoint returns one
// HoldActionResult per listing whose status is verified / mismatch / skipped /
// error; summarise those buckets for the operator.
export function showVerifyHoldsOutcomeToast(result: BulkHoldResult) {
  const counts = { verified: 0, mismatch: 0, skipped: 0, error: 0 };
  for (const r of result.results) {
    if (r.status in counts) counts[r.status as keyof typeof counts] += 1;
  }

  if (result.results.length === 0) {
    toast.info("No held listings to refresh.");
    return;
  }

  const parts: string[] = [];
  if (counts.verified) parts.push(`${counts.verified} verified`);
  if (counts.mismatch) parts.push(`${counts.mismatch} mismatched`);
  if (counts.skipped) parts.push(`${counts.skipped} skipped`);
  if (counts.error) parts.push(`${counts.error} failed`);
  const summary = parts.join(", ");

  if (counts.skipped && !counts.verified && !counts.mismatch && !counts.error) {
    toast.info("Holds not refreshed — Reach isn't configured.");
  } else if (counts.mismatch || counts.error) {
    toast.warning(`Holds refreshed — ${summary}. Please verify the flagged listings.`);
  } else {
    toast.success(`Holds refreshed — ${summary}.`);
  }
}

export function showHoldOutcomeToast(
  outcome: HoldActionResult,
  action: HoldAction,
) {
  const pastVerb = action === "hold" ? "placed" : "released";
  const presentVerb = action === "hold" ? "place" : "release";

  switch (outcome.status) {
    case "verified":
      toast.success(action === "hold" ? "Hold placed" : "Hold released");
      break;
    case "mismatch": {
      const reachState = outcome.adminHoldActive ? "held" : "not held";
      toast.warning(
        `Reach reports this listing as ${reachState}, which doesn't match the hold ${presentVerb} you requested — please verify manually.`,
      );
      break;
    }
    case "error":
      toast.error(
        `Failed to ${presentVerb} hold${outcome.error ? `: ${outcome.error}` : ""}`,
      );
      break;
    case "skipped":
      toast.info(
        `Hold ${pastVerb} locally — Reach isn't configured, so the change wasn't sent.`,
      );
      break;
    default:
      toast.info(`Hold ${pastVerb}`);
  }
}

export function showBulkHoldOutcomeToast(
  result: BulkHoldResult,
  action: HoldAction,
) {
  const pastVerb = action === "hold" ? "placed" : "released";
  const presentVerb = action === "hold" ? "place" : "release";
  const { successCount, failureCount } = result;

  if (failureCount === 0) {
    toast.success(
      `${successCount} hold${successCount === 1 ? "" : "s"} ${pastVerb}`,
    );
  } else if (successCount === 0) {
    toast.error(
      `Failed to ${presentVerb} ${failureCount} hold${failureCount === 1 ? "" : "s"} — check Reach and try again.`,
    );
  } else {
    toast.warning(
      `${successCount} hold${successCount === 1 ? "" : "s"} ${pastVerb}, ${failureCount} failed — please verify manually.`,
    );
  }
}
